import { Controller } from '@hotwired/stimulus';

// "Where am I?" → PATCH urlValue { lat, lng }. One fix (locate) or a stream
// (live). docs/HOTWIRE.md § locator — every host's, one name.
//
//   <div data-controller="locator bridge--location"
//        data-locator-url-value="<%= location_path %>"
//        data-locator-reload-value="true" data-locator-busy-class="loading">
//     <button data-locator-target="button" data-action="locator#locate">…</button>
//   </div>
//
// Optional: a status target (the line, said with the word values) and a live
// target (the toggle, data-action="locator#live").
//
//   locate ─ native? bridge.send requestLocation ─┐
//          └ else navigator.geolocation ──────────┴─► PATCH ─► located | failed
//   live   ─ native? location:startLiveLocation ─► bridge--location streams,
//            its location:* events come back on document
//          └ else a fix now + one every 30 s
//
// Refusing is an ordinary answer, not an error: the button goes quiet.
export default class extends Controller {
  static values = {
    url: String,
    reload: Boolean,
    ready: String,
    requesting: String,
    starting: String,
    stopping: String,
    updated: String,
    updatedLive: String,
    liveOn: String,
    liveOff: String,
    error: String,
    start: String,
    stop: String,
    unsupported: String,
    network: String,
  };
  static targets = ['button', 'status', 'live'];
  static classes = ['busy'];

  // bridge--location's word for what happened in the shell, during live.
  bridgeEvents = {
    'location:locationUpdated': ({ detail }) => this.said(detail.latitude, detail.longitude),
    'location:locationError': ({ detail }) => this.fail(detail?.error),
    'location:liveLocationStarted': () => this.living(true),
    'location:liveLocationStopped': () => this.living(false),
  };

  connect() {
    this.say(this.readyValue, 'idle');
    Object.entries(this.bridgeEvents).forEach(([name, fn]) => document.addEventListener(name, fn));
  }

  disconnect() {
    if (this.on) this.halt();
    Object.entries(this.bridgeEvents).forEach(([name, fn]) => document.removeEventListener(name, fn));
  }

  // The shell said so in its user agent — a browser that only loaded the
  // bridge library is still a browser.
  get bridge() {
    const bridge = this.application.getControllerForElementAndIdentifier(this.element, 'bridge--location');
    return bridge?.enabled ? bridge : null;
  }

  locate() {
    this.busy(true);
    this.say(this.requestingValue, 'busy');

    // Asked directly, not through the component's own POST: one request, to
    // our url, in our shape (push_controller does the same with bridge--push).
    if (this.bridge) {
      this.bridge.send('requestLocation', {}, ({ data } = {}) =>
        data?.latitude && data?.longitude ? this.send(data.latitude, data.longitude) : this.fail(),
      );
      return;
    }
    this.fix(300000, () => this.fail());
  }

  // The toggle: tap to start, tap to stop. Native: the bridge streams and
  // PATCHes on its own.
  live() {
    if (this.on) return this.halt();

    this.say(this.startingValue, 'busy');
    if (this.bridge) return this.element.dispatchEvent(new CustomEvent('location:startLiveLocation'));
    if (!navigator.geolocation) return this.fail(this.unsupportedValue);

    this.living(true);
    const fix = () => this.fix(60000, () => {});
    this.timer = setInterval(fix, 30000);
    fix();
  }

  halt() {
    this.say(this.stoppingValue, 'warn');
    if (this.bridge) return this.element.dispatchEvent(new CustomEvent('location:stopLiveLocation'));

    clearInterval(this.timer);
    this.living(false);
  }

  // One fix, sent. `no` is what a refusal does: the button's answer or nothing.
  fix(maximumAge, no) {
    if (!navigator.geolocation) return this.fail(this.unsupportedValue);

    navigator.geolocation.getCurrentPosition(({ coords }) => this.send(coords.latitude, coords.longitude), no, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge,
    });
  }

  async send(lat, lng) {
    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ lat, lng }),
      });
      // The server's own word when it has one (qir: radar.locator.bad_point).
      if (!response.ok) return this.fail((await response.json().catch(() => ({}))).error);
    } catch {
      return this.fail(this.networkValue);
    }

    this.said(lat, lng);
    if (this.reloadValue && !this.on) window.location.reload();
  }

  // The word only: the point is for the page (located), not for her to read.
  said(lat, lng) {
    this.busy(false);
    this.say(this.on ? this.updatedLiveValue : this.updatedValue, 'ok');
    this.dispatch('located', { detail: { lat, lng } });
  }

  fail(why) {
    this.busy(false);
    this.say([this.errorValue, why].filter(Boolean).join(': '), 'fail');
    if (this.hasButtonTarget) this.buttonTarget.disabled = true;
    this.dispatch('failed');
  }

  living(on) {
    this.on = on;
    this.say(on ? this.liveOnValue : this.liveOffValue, on ? 'ok' : 'idle');
    if (!this.hasLiveTarget) return;

    this.liveTarget.textContent = on ? this.stopValue : this.startValue;
    this.liveTarget.classList.toggle('btn-secondary', on);
    this.liveTarget.setAttribute('aria-pressed', on);
  }

  busy(on) {
    if (this.hasButtonTarget && this.hasBusyClass) this.buttonTarget.classList.toggle(this.busyClass, on);
  }

  // The line's five voices. Theme tokens only: a palette class is invisible
  // in at least one of the two themes a page wears.
  say(text, role) {
    if (!this.hasStatusTarget) return;

    const tone = {
      idle: 'text-base-content/70',
      busy: 'text-info',
      warn: 'text-warning',
      ok: 'text-success',
      fail: 'text-error',
    }[role];
    this.statusTarget.textContent = text;
    this.statusTarget.className = `text-sm ${tone}`;
  }
}
